import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Sparkles, 
  Calendar,
  BarChart3,
  Settings,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  Clock,
  Globe,
  Loader2,
  ChevronRight,
  Trash2,
  MoreHorizontal,
  AlertTriangle,
  Crown,
  Target
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient } from '@/lib/api';
import { toast } from 'sonner';
import AppShellLayout from '@/components/ios26/AppShellLayout';
import { GlassCard, PillButton, StatusChip, SectionHeader } from '@/components/ios26/IOSComponents';
import '@/styles/ios26-design-system.css';

/**
 * Dashboard Content - iOS 26-Inspired
 * ====================================
 * Main dashboard showing projects and quick actions
 * USES SEMANTIC TOKENS for dark mode compatibility
 * 
 * P0 FIX: Added plan limit display, project deletion menu, project persistence
 */
function DashboardContent() {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total_published: 0,
    total_engagement: 0,
    platforms_connected: 0
  });
  const [openMenuId, setOpenMenuId] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [deleteInput, setDeleteInput] = useState('');
  const [deleting, setDeleting] = useState(false);
  
  // Plan configuration
  const PLAN_LIMITS = { starter: 1, pro: 3, agency: 999, premium_predictive: 5 };
  const PLAN_NAMES = { starter: 'Starter', pro: 'Pro', agency: 'Agence', premium_predictive: 'Premium' };
  
  const currentPlan = user?.plan_tier || 'starter';
  const projectLimit = PLAN_LIMITS[currentPlan] || 1;
  const projectCount = projects.length;
  const canCreateProject = projectCount < projectLimit;
  const isAtLimit = projectCount >= projectLimit;
  
  useEffect(() => {
    loadProjects();
    loadStats();
  }, []);
  
  // P0 FIX: Persist selected project to backend/localStorage
  const selectProject = async (projectId) => {
    try {
      localStorage.setItem('selected_project_id', projectId);
      // Try to update on backend too (if endpoint exists)
      try {
        await apiClient.put('/auth/me', { selected_project_id: projectId });
      } catch (e) {
        // Backend update optional - localStorage is fallback
        console.debug('selected_project_id backend update not available');
      }
    } catch (error) {
      console.error('Failed to persist project selection:', error);
    }
  };
  
  const loadProjects = async () => {
    try {
      const response = await apiClient.get('/projects');
      const loadedProjects = response.data || [];
      setProjects(loadedProjects);
      
      // P0 FIX: Restore selected project from localStorage/user
      const savedProjectId = localStorage.getItem('selected_project_id') || user?.selected_project_id;
      if (savedProjectId && loadedProjects.length > 0) {
        const exists = loadedProjects.find(p => p.id === savedProjectId || p.project_id === savedProjectId);
        if (!exists && loadedProjects.length > 0) {
          // Saved project doesn't exist anymore - select the first one
          const firstProject = loadedProjects[0];
          selectProject(firstProject.id || firstProject.project_id);
        }
      } else if (loadedProjects.length > 0) {
        // No saved selection - select the most recent one
        const firstProject = loadedProjects[0];
        selectProject(firstProject.id || firstProject.project_id);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des projets');
    } finally {
      setLoading(false);
    }
  };
  
  const loadStats = async () => {
    try {
      const response = await apiClient.get('/user/stats');
      setStats(response.data || {});
    } catch (error) {
      // Stats might not be available
    }
  };
  
  const getProjectStatus = (project) => {
    if (!project.website_analyzed) return { status: 'pending', label: 'À configurer', step: 1 };
    if (!project.channels_connected) return { status: 'warning', label: 'Connexions requises', step: 2 };
    if (!project.campaign_created) return { status: 'warning', label: 'Campagne à créer', step: 3 };
    if (!project.content_generated) return { status: 'pending', label: 'Contenu à générer', step: 4 };
    return { status: 'success', label: 'Actif', step: 5 };
  };
  
  const handleProjectClick = (project) => {
    selectProject(project.id || project.project_id);
    // P0 FIX: Always go to calendar (new UI), not guided setup
    navigate(`/projects/${project.id || project.project_id}/calendar`);
  };
  
  // P0 FIX: Delete project functionality
  const handleDeleteClick = (e, project) => {
    e.stopPropagation();
    setOpenMenuId(null);
    setDeleteConfirmId(project.id || project.project_id);
    setDeleteConfirmName(project.name);
    setDeleteInput('');
  };
  
  const handleConfirmDelete = async () => {
    if (deleteInput !== deleteConfirmName) {
      toast.error('Le nom ne correspond pas');
      return;
    }
    
    setDeleting(true);
    try {
      await apiClient.delete(`/projects/${deleteConfirmId}`);
      toast.success('Projet supprimé avec succès');
      
      // CHECK 8: Immediately update UI without full reload (optimistic update)
      setProjects(prev => prev.filter(p => (p.id || p.project_id) !== deleteConfirmId));
      
      // Clear localStorage if deleted project was selected
      const savedProjectId = localStorage.getItem('selected_project_id');
      if (savedProjectId === deleteConfirmId) {
        localStorage.removeItem('selected_project_id');
      }
      
      setDeleteConfirmId(null);
      setDeleteConfirmName('');
      setDeleteInput('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };
  
  const handleCancelDelete = () => {
    setDeleteConfirmId(null);
    setDeleteConfirmName('');
    setDeleteInput('');
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'hsl(var(--color-primary))' }} />
      </div>
    );
  }
  
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Welcome Header */}
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-semantic mb-2">
          Bonjour{user?.email ? `, ${user.email.split('@')[0]}` : ''} 👋
        </h1>
        <p className="text-semantic-2">
          Gérez vos projets marketing et suivez vos performances
        </p>
      </div>
      
      {/* Quick Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <GlassCard className="p-5">
          <div className="flex items-center gap-3">
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: 'hsl(var(--color-info-bg))' }}
            >
              <Sparkles className="w-5 h-5" style={{ color: 'hsl(var(--color-primary))' }} />
            </div>
            <div>
              <p className="text-2xl font-bold text-semantic">{projects.length}</p>
              <p className="text-xs text-semantic-muted">Projets</p>
            </div>
          </div>
        </GlassCard>
        
        <GlassCard className="p-5">
          <div className="flex items-center gap-3">
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: 'hsl(var(--color-success-bg))' }}
            >
              <CheckCircle className="w-5 h-5" style={{ color: 'hsl(var(--color-success))' }} />
            </div>
            <div>
              <p className="text-2xl font-bold text-semantic">{stats.total_published || 0}</p>
              <p className="text-xs text-semantic-muted">Publiés</p>
            </div>
          </div>
        </GlassCard>
        
        <GlassCard className="p-5">
          <div className="flex items-center gap-3">
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: 'hsl(270 50% 92%)' }}
            >
              <BarChart3 className="w-5 h-5" style={{ color: 'hsl(270 60% 55%)' }} />
            </div>
            <div>
              <p className="text-2xl font-bold text-semantic">{stats.total_engagement || 0}</p>
              <p className="text-xs text-semantic-muted">Engagement</p>
            </div>
          </div>
        </GlassCard>
        
        <GlassCard className="p-5">
          <div className="flex items-center gap-3">
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: 'hsl(var(--color-warning-bg))' }}
            >
              <Globe className="w-5 h-5" style={{ color: 'hsl(var(--color-warning))' }} />
            </div>
            <div>
              <p className="text-2xl font-bold text-semantic">{stats.platforms_connected || 0}</p>
              <p className="text-xs text-semantic-muted">Plateformes</p>
            </div>
          </div>
        </GlassCard>
      </div>
      
      {/* Projects Section */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold text-semantic">Mes projets</h2>
            {/* P0 FIX: Plan limit indicator */}
            <span 
              className="text-xs px-2.5 py-1 rounded-full font-medium"
              style={{ 
                backgroundColor: isAtLimit ? 'hsl(var(--color-warning-bg))' : 'hsl(var(--color-surface-2))',
                color: isAtLimit ? 'hsl(var(--color-warning-text))' : 'hsl(var(--color-text-muted))'
              }}
              data-testid="plan-limit-indicator"
            >
              {projectCount}/{projectLimit} projet{projectLimit > 1 ? 's' : ''} ({PLAN_NAMES[currentPlan]})
            </span>
          </div>
          {canCreateProject ? (
            <PillButton
              variant="primary"
              size="sm"
              onClick={() => navigate('/projects/new')}
              icon={<Plus className="w-4 h-4" />}
              data-testid="new-project-btn"
            >
              Nouveau projet
            </PillButton>
          ) : (
            <PillButton
              variant="outline"
              size="sm"
              onClick={() => navigate('/pricing')}
              icon={<Crown className="w-4 h-4" />}
              data-testid="upgrade-plan-btn"
            >
              Passer au Pro
            </PillButton>
          )}
        </div>
        
        {/* P0 FIX: Plan limit reached banner */}
        {isAtLimit && (
          <div 
            className="flex items-center gap-3 p-4 rounded-xl mb-4"
            style={{ 
              backgroundColor: 'hsl(var(--color-warning-bg))',
              border: '1px solid hsl(var(--color-warning) / 0.3)'
            }}
            data-testid="plan-limit-banner"
          >
            <AlertTriangle className="w-5 h-5 flex-shrink-0" style={{ color: 'hsl(var(--color-warning))' }} />
            <div className="flex-1">
              <p className="font-medium text-sm" style={{ color: 'hsl(var(--color-warning-text))' }}>
                Limite de projet atteinte
              </p>
              <p className="text-xs" style={{ color: 'hsl(var(--color-warning-text))' }}>
                Votre plan {PLAN_NAMES[currentPlan]} permet {projectLimit} projet{projectLimit > 1 ? 's' : ''}. 
                Supprimez un projet existant ou passez au plan Pro pour en créer plus.
              </p>
            </div>
            <PillButton
              variant="outline"
              size="sm"
              onClick={() => navigate('/pricing')}
            >
              Voir les plans
            </PillButton>
          </div>
        )}
        
        {projects.length === 0 ? (
          <GlassCard className="p-8 text-center">
            <div 
              className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: 'hsl(var(--color-info-bg))' }}
            >
              <Sparkles className="w-8 h-8" style={{ color: 'hsl(var(--color-primary))' }} />
            </div>
            <h3 className="text-lg font-semibold text-semantic mb-2">
              Créez votre premier projet
            </h3>
            <p className="text-semantic-2 mb-6 max-w-sm mx-auto">
              Analysez votre site, connectez vos réseaux sociaux et générez 30 jours de contenu automatiquement.
            </p>
            <PillButton
              variant="primary"
              onClick={() => navigate('/projects/new')}
              icon={<Plus className="w-5 h-5" />}
            >
              Créer un projet
            </PillButton>
          </GlassCard>
        ) : (
          <div className="grid gap-4">
            {projects.map((project) => {
              const projectStatus = getProjectStatus(project);
              const projectId = project.id || project.project_id;
              const isMenuOpen = openMenuId === projectId;
              
              return (
                <GlassCard 
                  key={projectId}
                  className="p-5 cursor-pointer relative"
                  hover
                  onClick={() => handleProjectClick(project)}
                  data-testid={`project-card-${projectId}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg">
                        {project.name?.charAt(0)?.toUpperCase() || 'P'}
                      </div>
                      <div>
                        <h3 className="font-semibold text-semantic">{project.name}</h3>
                        <p className="text-sm text-semantic-muted truncate max-w-[300px]">
                          {project.website_url || 'Pas de site configuré'}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <StatusChip status={projectStatus.status}>
                        {projectStatus.label}
                      </StatusChip>
                      
                      {/* P0 FIX: Project menu with delete option */}
                      <div className="relative">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenuId(isMenuOpen ? null : projectId);
                          }}
                          className="p-2 rounded-lg transition-colors hover:bg-white/10"
                          style={{ backgroundColor: isMenuOpen ? 'hsl(var(--color-surface-2))' : 'transparent' }}
                          data-testid={`project-menu-btn-${projectId}`}
                        >
                          <MoreHorizontal className="w-5 h-5 text-semantic-muted" />
                        </button>
                        
                        {isMenuOpen && (
                          <div 
                            className="absolute right-0 top-full mt-1 py-1 rounded-xl shadow-lg z-10 min-w-[160px]"
                            style={{ 
                              backgroundColor: 'hsl(var(--color-surface))',
                              border: '1px solid hsl(var(--color-border))'
                            }}
                          >
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/projects/${projectId}/settings`);
                              }}
                              className="w-full px-4 py-2 text-left text-sm flex items-center gap-2 transition-colors hover:bg-white/5"
                              style={{ color: 'hsl(var(--color-text))' }}
                            >
                              <Settings className="w-4 h-4" />
                              Paramètres
                            </button>
                            <button
                              onClick={(e) => handleDeleteClick(e, project)}
                              className="w-full px-4 py-2 text-left text-sm flex items-center gap-2 transition-colors hover:bg-white/5"
                              style={{ color: 'hsl(var(--color-destructive))' }}
                              data-testid={`delete-project-btn-${projectId}`}
                            >
                              <Trash2 className="w-4 h-4" />
                              Supprimer
                            </button>
                          </div>
                        )}
                      </div>
                      
                      <ChevronRight className="w-5 h-5 text-semantic-muted" />
                    </div>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="mt-4 flex items-center gap-2">
                    {[1, 2, 3, 4, 5].map((step) => (
                      <div 
                        key={step}
                        className="flex-1 h-1.5 rounded-full transition-colors"
                        style={{
                          backgroundColor: step < projectStatus.step 
                            ? 'hsl(var(--color-success))' 
                            : step === projectStatus.step 
                              ? 'hsl(var(--color-primary))' 
                              : 'hsl(var(--color-surface-3))'
                        }}
                      />
                    ))}
                  </div>
                  <div className="mt-2 flex items-center gap-4 text-xs">
                    {['Analyse', 'Connexion', 'Campagne', 'Contenu', 'Résultats'].map((label, idx) => (
                      <span 
                        key={label}
                        style={{ 
                          color: projectStatus.step > idx + 1 
                            ? 'hsl(var(--color-success))' 
                            : 'hsl(var(--color-text-muted))'
                        }}
                      >
                        {label}
                      </span>
                    ))}
                  </div>
                </GlassCard>
              );
            })}
          </div>
        )}
      </div>
      
      {/* P0 FIX: Delete confirmation modal */}
      {deleteConfirmId && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={handleCancelDelete}
        >
          <div 
            className="w-full max-w-md rounded-2xl p-6"
            style={{ backgroundColor: 'hsl(var(--color-surface))' }}
            onClick={(e) => e.stopPropagation()}
            data-testid="delete-confirm-modal"
          >
            <div className="flex items-center gap-3 mb-4">
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: 'hsl(var(--color-destructive) / 0.1)' }}
              >
                <Trash2 className="w-6 h-6" style={{ color: 'hsl(var(--color-destructive))' }} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-semantic">Supprimer le projet</h3>
                <p className="text-sm text-semantic-muted">Cette action est irréversible</p>
              </div>
            </div>
            
            <p className="text-semantic-2 mb-4">
              Pour confirmer la suppression, tapez le nom du projet :
            </p>
            <p className="text-semantic font-mono font-semibold mb-3 p-2 rounded-lg" style={{ backgroundColor: 'hsl(var(--color-surface-2))' }}>
              {deleteConfirmName}
            </p>
            
            <input
              type="text"
              value={deleteInput}
              onChange={(e) => setDeleteInput(e.target.value)}
              placeholder="Tapez le nom du projet"
              className="w-full input-semantic mb-4"
              data-testid="delete-confirm-input"
              autoFocus
            />
            
            <div className="flex gap-3">
              <PillButton
                variant="outline"
                className="flex-1"
                onClick={handleCancelDelete}
              >
                Annuler
              </PillButton>
              <PillButton
                variant="destructive"
                className="flex-1"
                onClick={handleConfirmDelete}
                loading={deleting}
                disabled={deleteInput !== deleteConfirmName}
                data-testid="confirm-delete-btn"
              >
                {deleting ? 'Suppression...' : 'Supprimer'}
              </PillButton>
            </div>
          </div>
        </div>
      )}
      
      {/* Quick Actions */}
      <div>
        <h2 className="text-xl font-bold text-semantic mb-4">Actions rapides</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* P0 CRM: Prospecting Agent quick access */}
          <button
            onClick={() => navigate('/prospecting')}
            className="p-5 rounded-2xl text-left transition-all hover:scale-[1.01]"
            style={{
              backgroundColor: 'hsl(142, 70%, 97%)',
              border: '1px solid hsl(142, 70%, 80%)'
            }}
            data-testid="dashboard-prospecting-quick-action"
          >
            <div className="flex items-center justify-between mb-3">
              <Target className="w-8 h-8" style={{ color: 'hsl(142, 70%, 45%)' }} />
              <span
                className="text-xs font-bold px-2 py-0.5 rounded-full"
                style={{
                  backgroundColor: 'hsl(142, 70%, 45%)',
                  color: 'white'
                }}
              >
                NOUVEAU
              </span>
            </div>
            <h3 className="font-semibold text-semantic mb-1">Agent de Prospection CRM</h3>
            <p className="text-sm text-semantic-2">Trouvez de nouveaux prospects et synchronisez Brevo</p>
          </button>

          <button
            onClick={() => projects.length > 0 && navigate(`/projects/${projects[0]?.id || projects[0]?.project_id}/calendar`)}
            disabled={projects.length === 0}
            className="p-5 rounded-2xl text-left transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              backgroundColor: 'hsl(var(--color-surface))',
              border: '1px solid hsl(var(--color-border))'
            }}
          >
            <Calendar className="w-8 h-8 mb-3" style={{ color: 'hsl(var(--color-primary))' }} />
            <h3 className="font-semibold text-semantic mb-1">Voir le calendrier</h3>
            <p className="text-sm text-semantic-2">Planifiez et gérez vos publications</p>
          </button>
          
          <button
            onClick={() => projects.length > 0 && navigate(`/projects/${projects[0]?.id || projects[0]?.project_id}/analytics`)}
            disabled={projects.length === 0}
            className="p-5 rounded-2xl text-left transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              backgroundColor: 'hsl(var(--color-surface))',
              border: '1px solid hsl(var(--color-border))'
            }}
          >
            <BarChart3 className="w-8 h-8 mb-3" style={{ color: 'hsl(270 60% 55%)' }} />
            <h3 className="font-semibold text-semantic mb-1">Analytics</h3>
            <p className="text-sm text-semantic-2">Suivez vos performances</p>
          </button>
          
          <button
            onClick={() => projects.length > 0 && navigate(`/projects/${projects[0]?.id || projects[0]?.project_id}/setup/connect`)}
            disabled={projects.length === 0}
            className="p-5 rounded-2xl text-left transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              backgroundColor: 'hsl(var(--color-surface))',
              border: '1px solid hsl(var(--color-border))'
            }}
          >
            <Settings className="w-8 h-8 mb-3" style={{ color: 'hsl(var(--color-success))' }} />
            <h3 className="font-semibold text-semantic mb-1">Gérer les connexions</h3>
            <p className="text-sm text-semantic-2">Configurez vos plateformes</p>
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Dashboard Page with AppShell
 */
export default function DashboardV2() {
  return (
    <AppShellLayout>
      <DashboardContent />
    </AppShellLayout>
  );
}
